import { Coffee } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="page-transition py-20 px-6 md:px-12">
      <div className="container mx-auto max-w-4xl">
        <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight text-primary mb-8" data-testid="about-title">
          Why We Curate
        </h1>
        
        <div className="space-y-6 text-lg leading-relaxed text-muted-foreground">
          <p data-testid="about-paragraph-1">
            Gift-giving, especially for children and new moms, can feel overwhelming. The marketplace is cluttered with options,
            and it's hard to know what's truly meaningful versus what's just marketed well.
          </p>
          
          <p data-testid="about-paragraph-2">
            <strong className="text-foreground">what2gift</strong> exists to solve this problem. We're not a marketplace.
            We're curators who understand child development, the postpartum journey, and what makes a gift truly special.
          </p>
          
          <h2 className="font-heading text-3xl font-light text-primary pt-8 pb-4">Our Principles</h2>
          
          <ul className="space-y-4 list-none">
            <li className="flex gap-3">
              <span className="text-accent font-bold">•</span>
              <span><strong className="text-foreground">Context Over Commerce:</strong> Every recommendation explains why it matters.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-accent font-bold">•</span>
              <span><strong className="text-foreground">Developmental Relevance:</strong> Age-appropriate and meaningful for each stage.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-accent font-bold">•</span>
              <span><strong className="text-foreground">Thoughtfulness First:</strong> We prioritize items that show genuine care.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-accent font-bold">•</span>
              <span><strong className="text-foreground">Quality Over Quantity:</strong> A curated list beats endless scrolling.</span>
            </li>
          </ul>
          
          <p className="pt-8" data-testid="about-paragraph-3">
            Whether you're child-free and unsure what to get, or a parent seeking meaningful books, we're here to make
            gift-giving feel confident and intentional again.
          </p>

          <h2 className="font-heading text-3xl font-light text-primary pt-8 pb-4">A Note on Reading &amp; Ages</h2>

          <p data-testid="about-paragraph-reading">
            Reading with children from a very young age is one of the most powerful gifts you can offer. Long before a
            child can recognise a single word, shared stories build language, imagination, empathy and a lasting bond.
            The magic isn't in the child reading — it's in the moments of reading together.
          </p>

          <p data-testid="about-paragraph-ages">
            Please treat the ages we mention as <strong className="text-foreground">indicative and suggestive, not strict rules</strong>.
            They're a gentle guide to help you choose, not a signal that a child of that age should be reading on their own.
            The vast majority of these books are meant for <strong className="text-foreground">parents and caregivers to read aloud</strong>
            {' '}— to point at pictures, ask questions, make sounds and spark conversation. Follow your child's curiosity;
            a "younger" book can delight an older child, and a "older" story can be wonderful when read together.
          </p>
          <div className="mt-10 rounded-xl bg-secondary/40 border border-border/60 p-6 md:p-8 text-center" data-testid="about-kofi-callout">
            <h2 className="font-heading text-2xl font-light text-primary mb-2">Enjoying our recommendations?</h2>
            <p className="text-base text-muted-foreground mb-5">
              what2gift is a labour of love. If our curation has helped you find something meaningful,
              you can support us with a small contribution — it keeps us curating thoughtfully.
            </p>
            <a
              href="https://ko-fi.com/mirnalinivenkatraman"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-accent text-accent-foreground font-body text-base font-semibold px-6 py-3 rounded-full hover:opacity-90 transition-opacity"
              data-testid="about-kofi-link"
            >
              <Coffee className="h-5 w-5" /> Support Us on Ko-fi
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}